import { StateCreator } from 'zustand'
import { SharedSlice } from 'src/components/slice'
import { IRosteredShiftResponse, IStaffMemberDetail } from '$models'
import { Dayjs } from 'dayjs'
import { rosterService, rosteredShiftService } from '$services'
import { enqueueLoadedSnackbar, exceptionMessageHandler } from '$utils'
import { DayOfWeek, ShiftType } from '$constants'

interface CopyRosterState {
  sourceRosteredShifts: IRosteredShiftResponse[]
  destinationRosteredShifts: IRosteredShiftResponse[]
  staffMembers: IStaffMemberDetail[]
  isSourceRosteredShiftsLoading: boolean
  isDestinationRosteredShiftsLoading: boolean
}

export interface CopyRosterSlice extends CopyRosterState {
  /**
   * Reset CopyRoster Slice
   */
  resetCopyRosterSlice: () => void

  /**
   * Get Rosters by date range
   * @param startTime
   * @param endTime
   * @param type 'source' | 'destination' this helps us to know which rostered shifts
   * we need to populate (the source or destination rostered shifts)
   * @returns Promise<IRosteredShiftResponse[]>
   */
  getRosteredShitsByDateRange: (
    startDateTime: Dayjs | null,
    endDateTime: Dayjs | null,
    type: 'source' | 'destination'
  ) => Promise<IRosteredShiftResponse[]>

  /**
   * Copy Roster
   * @param rosterTemplateId
   * @param sourceWeekStart
   * @param destinationWeekStart
   * @param days
   * @param staffMemberRosteredShiftsInfo
   * @returns Promise<boolean>
   */
  copyRoster: (
    rosterTemplateId: string | undefined,
    sourceWeekStart: Dayjs | undefined,
    destinationWeekStart: Dayjs,
    days: DayOfWeek[],
    roleIds: string[] | undefined,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<boolean>

  /**
   * Set Rostered Shifts
   * @param type 'source' | 'destination' this helps us to know which rostered shifts
   * we need to populate (the source or destination rostered shifts)
   * @param rosteredShifts IRosteredShiftResponse[] to set
   */
  setRosteredShifts: (
    type: 'source' | 'destination',
    rosteredShifts: IRosteredShiftResponse[]
  ) => void
}

const initialState: CopyRosterState = {
  sourceRosteredShifts: [],
  destinationRosteredShifts: [],
  staffMembers: [],
  isSourceRosteredShiftsLoading: false,
  isDestinationRosteredShiftsLoading: false,
}

const createCopyRosterSlice: StateCreator<
  CopyRosterSlice & SharedSlice,
  [],
  [],
  CopyRosterSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State setters
  resetCopyRosterSlice: () => set(initialState),

  /**
   * We get the rostered shifts from the roster itself
   */
  getRosteredShitsByDateRange: async (
    startTime: Dayjs | null,
    endTime: Dayjs | null,
    type: 'source' | 'destination'
  ): Promise<IRosteredShiftResponse[]> => {
    if (!startTime || !endTime) {
      if (type === 'source') {
        set(() => ({
          sourceRosteredShifts: [],
        }))
      } else if (type === 'destination') {
        set(() => ({
          destinationRosteredShifts: [],
        }))
      }
      return []
    }

    if (type === 'source') {
      set(() => ({
        isSourceRosteredShiftsLoading: true,
      }))
    } else if (type === 'destination') {
      set(() => ({
        isDestinationRosteredShiftsLoading: true,
      }))
    }

    return rosteredShiftService
      .getAll(startTime, endTime)
      .then((result) => {
        result = result.filter((shift) => shift.type === ShiftType.Roster)
        if (type === 'source') {
          set(() => ({
            sourceRosteredShifts: result,
            isSourceRosteredShiftsLoading: false,
          }))
        } else if (type === 'destination') {
          set(() => ({
            destinationRosteredShifts: result,
            isDestinationRosteredShiftsLoading: false,
          }))
        }
        return result
      })
      .catch((error) => {
        set(() => ({
          sourceRosteredShifts: [],
        }))
        exceptionMessageHandler(error, 'Failed to get the roster')
        return []
      })
      .finally(() => {
        set(() => ({
          isSourceRosteredShiftsLoading: false,
          isDestinationRosteredShiftsLoading: false,
        }))
      })
  },

  copyRoster: async (
    rosterTemplateId: string | undefined,
    sourceWeekStart: Dayjs | undefined,
    destinationWeekStart: Dayjs,
    days: DayOfWeek[],
    roleIds: string[] | undefined,
    successMessage?: string,
    errorMessage?: string
  ): Promise<boolean> => {
    set(() => ({
      isLoading: true,
    }))
    return rosterService
      .copyRoster(
        rosterTemplateId,
        sourceWeekStart,
        destinationWeekStart,
        days,
        roleIds
      )
      .then(() => {
        // We don't set isLoading to false here because we want to redirect to the
        // angular page without flashing the copy page first
        enqueueLoadedSnackbar(successMessage, 'success')
        return true
      })
      .catch((error) => {
        exceptionMessageHandler(error, errorMessage)
        set(() => ({
          isLoading: false,
        }))
        return false
      })
  },

  setRosteredShifts: (
    type: 'source' | 'destination',
    rosteredShifts: IRosteredShiftResponse[]
  ): void => {
    if (type === 'source') {
      set(() => ({
        sourceRosteredShifts: rosteredShifts,
      }))
    } else if (type === 'destination') {
      set(() => ({
        destinationRosteredShifts: rosteredShifts,
      }))
    }
  },
})

export default createCopyRosterSlice
